import { useMutation, useQueryClient } from "react-query";

import { createTargetMarker } from "api/targets";
import { useGlobalToast } from "components/GlobalToastProvider";
import type { CreateTargetMarkerBody } from "api/types/markers";
import { invalidateTargetMarkers } from "./invalidateTargetMarkers";

interface CreateTargetMarkerPayload {
  targetId: number;
  data: CreateTargetMarkerBody;
}

/**
 * Creates a chart marker (phase change / external event) for a target.
 * Invalidates the markers query of every target it applies to on success, so
 * each chart overlay reflects the new marker.
 */
const useCreateTargetMarker = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: createMarkerAction,
    isLoading: isCreatingMarker,
    ...rest
  } = useMutation(
    ({ targetId, data }: CreateTargetMarkerPayload) => createTargetMarker(targetId, data),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Marker added" });
        invalidateTargetMarkers(queryClient, [
          variables.targetId,
          ...(response?.data?.target_ids ?? variables.data.target_ids ?? []),
        ]);
        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to add marker";
        showToast({ message: errorMessage, errorState: true });
        onError(error);
      },
      ...otherOptions,
    }
  );

  return { createMarkerAction, isCreatingMarker, ...rest };
};

export default useCreateTargetMarker;
